import {
    Dialog,
    DialogContent,
    DialogTitle,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { useDataProvider, useNotify } from "react-admin";

export const RunningVolumeDialog = ({
    open,
    onClose
}) => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const [batchOptions, setBatchOptions] = useState([]);
    const [selectedBatchId, setSelectedBatchId] = useState("");
    const [resultNumber, setResultNumber] = useState(0);
    const [failNumber, setFailNumber] = useState(0);
    const [loading, setLoading] = useState(false);


    // 重置所有状态
    const resetDialogState = () => {
        setSelectedBatchId("");
        setResultNumber(0);
        setFailNumber(0);
    };

    // 获取批次列表
    const fetchBatchOptions = async () => {
        try {
            setLoading(true);
            const response = await dataProvider.getBatchList();
            setBatchOptions(response.data.map(item => ({
                id: item.batchNo,
                name: `批次号: ${item.batchNo}_${item.strategyName}`
            })));
        } catch (error) {
            notify('获取批次列表失败', { type: 'error' });
            console.error("获取批次列表失败:", error);
        } finally {
            setLoading(false);
        }
    };

    // 获取批次统计信息
    const fetchBatchStats = async (batchId: string) => {
        if (!batchId) return;

        try {
            setLoading(true);
            const response = await dataProvider.getTaskStats(batchId);
            setResultNumber(response.data.success_count);
            setFailNumber(response.data.fail_count);
        } catch (error) {
            notify('获取批次统计信息失败', { type: 'error' });
            console.error("获取批次统计信息失败:", error);
        } finally {
            setLoading(false);
        }
    };

    // 当选择的批次号变化时获取统计信息
    useEffect(() => {
        if (selectedBatchId) {
            fetchBatchStats(selectedBatchId);
        }
    }, [selectedBatchId]);

    // 当对话框打开或任务ID变化时加载数据
    useEffect(() => {
        if (open) {
            resetDialogState();
            fetchBatchOptions();
        }
    }, [open]);



    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>流量结果</DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 3, mt: 2 }}>
                    <FormControl fullWidth>
                        <InputLabel id="batch-select-label">选择批次号</InputLabel>
                        <Select
                            labelId="batch-select-label"
                            label="选择批次号"
                            value={selectedBatchId}
                            onChange={(e) => setSelectedBatchId(e.target.value)}
                            disabled={loading || batchOptions.length === 0}
                            sx={{
                                minWidth: 300,
                                '& .MuiInputBase-root': {
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: '4px',
                                }
                            }}
                        >
                            {batchOptions.map((option) => (
                                <MenuItem key={option.id} value={option.id}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                )}

                {!loading && selectedBatchId && (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            p: 2,
                            border: '1px solid #e0e0e0',
                            borderRadius: '4px',
                            backgroundColor: '#fafafa'
                        }}
                    >
                        <Box sx={{ fontSize: 16, fontWeight: 'bold', mb: 1 }}>
                            批次号: {selectedBatchId}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 3, mt: 1 }}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Box sx={{ fontSize: 14, color: 'text.secondary' }}>成功设备数</Box>
                                <Box sx={{ fontSize: 24, fontWeight: 'bold', color: 'success.main' }}>
                                    {resultNumber}台
                                </Box>
                            </Box>
                            <Box sx={{ textAlign: 'center' }}>
                                <Box sx={{ fontSize: 14, color: 'text.secondary' }}>失败设备数</Box>
                                <Box sx={{ fontSize: 24, fontWeight: 'bold', color: 'error.main' }}>
                                    {failNumber}台
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                )}

                {!loading && !selectedBatchId && batchOptions.length > 0 && (
                    <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                        请选择一个批次号查看详情
                    </Box>
                )}

                {!loading && batchOptions.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                        暂无批次数据
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};